import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MdChevronLeft } from "react-icons/md";

import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/permissions";
import { useNotifications } from "../notifications/NotificationsContext";
import {
  acceptTransfer,
  cancelTransfer,
  fetchTransfer,
  fetchTransfers,
  rejectTransfer,
} from "../notifications/api";
import { actorName, fmtDateTime, timeAgo } from "../notifications/format";
import { transfersChanged } from "../notifications/useIncomingTransferCount";
import "../notifications.css";

/* ------------------------------------------------------------------ *
 * Work-order transfers, both directions.
 *
 *   /transfers            requests addressed to us (the inbox)
 *   /transfers/outgoing   requests we have sent to other businesses
 *   /transfers/:id        one request: preview of the order as it was when
 *                         asked, then Accept / Reject (destination) or
 *                         Withdraw (source)
 *
 * Which side we are on is decided by the ACTIVE BUSINESS, not the URL —
 * the server stamps every row with `direction` relative to it.
 *
 * Endpoints: GET /work-orders/transfers?direction=&status=, GET …/:id,
 * POST …/:id/accept, …/reject, …/cancel. All need `workorders:transfer`
 * (owner + admin by default). A member without it still lands here from
 * the notification — the screen then shows what the notification carried
 * and explains it cannot act.
 *
 * Accept is two-step when something blocks the move: the first call
 * returns 409 with `details`, the user ticks the fixes on offer, and the
 * call is re-sent with the matching flags.
 * ------------------------------------------------------------------ */

const PERM = "workorders:transfer";

const isFuneral = (t) => String(t || "").toLowerCase() === "funeral";
const text = (v) => (v === undefined || v === null || v === "" ? null : String(v));
const parseJson = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
};
const locationName = (raw) => {
  const v = parseJson(raw, raw);
  if (v && typeof v === "object") return v.name || v.address || v.formatted_address || null;
  return text(v);
};
/* The work order keeps the funeral director and the staff member who took
   it as JSON strings (fd_code, received_by) — shown raw, they read as code.
   `funeral_director` is always present but usually an empty subdocument,
   so it only wins when it actually carries a name. */
const directorName = (wo) => {
  const sub = wo.funeral_director || {};
  const fd = sub.business_name ? sub : parseJson(wo.fd_code, wo.fd_code) || {};
  if (typeof fd !== "object") return text(fd);
  const name = text(fd.business_name);
  const code = text(fd.code);
  return name && code ? `${name} (${code})` : name || code;
};
const staffName = (raw) => {
  const u = parseJson(raw, raw);
  if (!u || typeof u !== "object") return text(u);
  return (
    text(u.full_name) ||
    text(u.name) ||
    text([u.first_name, u.last_name].filter(Boolean).join(" ")) ||
    text(u.username) ||
    text(u.email)
  );
};
const businessLabel = (b) => (b && (b.name || b.code)) || "another business";

function StatusPill({ status }) {
  const s = String(status || "pending");
  return <span className={`tr-pill is-${s}`}>{s}</span>;
}

function KV({ label, value }) {
  const v = text(value);
  return (
    <div>
      <dt>{label}</dt>
      <dd className={v ? "" : "is-empty"}>{v || "—"}</dd>
    </div>
  );
}

/* ---------------- order preview (from the snapshot) ---------------- */

function OrderPreview({ transfer }) {
  const snap = (transfer && transfer.snapshot) || {};
  const wo = snap.work_order || null;
  const lines = Array.isArray(snap.ordered_products) ? snap.ordered_products : [];
  const funeral = isFuneral(transfer.work_order_type || (wo && wo.type));

  if (!wo) {
    return (
      <div className="tr-card">
        <h3 className="tr-card-title">Order</h3>
        <p className="tr-help">No preview was captured for this request.</p>
      </div>
    );
  }

  return (
    <>
      <div className="tr-card">
        <h3 className="tr-card-title">{funeral ? "Funeral" : "Delivery"}</h3>
        <dl className="tr-kv">
          {funeral ? (
            <>
              <KV label="Late person" value={wo.late_person_name} />
              <KV label="Funeral date" value={wo.funeral_date} />
              <KV label="Funeral time" value={wo.funeral_time} />
              <KV label="Funeral location" value={locationName(wo.funeral_location)} />
              <KV label="Funeral director" value={directorName(wo)} />
              <KV label="Casket cover delivery" value={locationName(wo.casket_cover_delivery_location)} />
              <KV label="Casket cover time" value={wo.casket_cover_delivery_time} />
              <KV label="Tributes delivery" value={locationName(wo.tribuite_delivery_location)} />
              <KV label="Tributes time" value={wo.tributes_delivery_time} />
            </>
          ) : (
            <>
              <KV label="Recipient" value={wo.recipient_name} />
              <KV label="Recipient phone" value={wo.recipient_phone} />
              <KV label="Delivery date" value={wo.delivery_date} />
              <KV label="Delivery time" value={wo.time_of_delivery} />
              <KV label="Delivery location" value={locationName(wo.delivery_location)} />
              <KV label="Area" value={wo.delivery_area} />
              <KV label="Frequency" value={wo.frequency} />
              <KV label="Card message" value={wo.card_message} />
            </>
          )}
        </dl>
      </div>

      <div className="tr-card">
        <h3 className="tr-card-title">Customer &amp; payment</h3>
        <dl className="tr-kv">
          <KV label="Ordered by" value={wo.ordered_by} />
          <KV label="Phone" value={wo.orderer_phone || wo.contact_phone_number} />
          <KV label="Company" value={wo.company_name} />
          <KV label="Received by" value={staffName(wo.received_by)} />
          <KV label="Payment" value={wo.payment_option} />
          <KV label="Invoice" value={wo.invoice_number} />
          <KV label="Total (inc. GST)" value={wo.total_price_with_GST || wo.total_price} />
          <KV label="Order date" value={wo.date} />
        </dl>
        {text(wo.order_comment) && (
          <p className="tr-note" style={{ marginTop: 14 }}>
            {wo.order_comment}
          </p>
        )}
      </div>

      <div className="tr-card">
        <h3 className="tr-card-title">Products ({lines.length})</h3>
        {lines.length === 0 ? (
          <p className="tr-help">No product lines on this order.</p>
        ) : (
          <table className="tr-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Product</th>
                <th>Type</th>
                <th>Size</th>
                <th className="is-num">Qty</th>
                <th className="is-num">Price</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((p, i) => (
                <tr key={p._id || i}>
                  <td className="tr-serial">{text(p.code) || "—"}</td>
                  <td>
                    {text(p.product_name) || "—"}
                    {text(p.sash_message) && (
                      <div className="is-muted">Sash: {p.sash_message}</div>
                    )}
                    {text(p.comment) && <div className="is-muted">{p.comment}</div>}
                  </td>
                  <td>{text(p.product_type) || "—"}</td>
                  <td>{text(p.size) || text(p.sizes) || "—"}</td>
                  <td className="is-num">{Number(p.amount) || 1}</td>
                  <td className="is-num">
                    {p.customPrice || p.price
                      ? `$${Number(p.customPrice || p.price).toFixed(2)}`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ---------------- blockers (409 details) ---------------- */

function Blockers({ details, flags, setFlags }) {
  const missing = details.missing_products || [];
  const runs = details.run_sheets || [];
  const unclonable = details.unclonable_products || [];
  return (
    <div className="tr-blockers">
      <p className="tr-blockers-title">Before this order can move</p>
      {missing.length > 0 && (
        <label className="tr-blocker">
          <input
            type="checkbox"
            checked={!!flags.clone_products}
            onChange={(e) => setFlags({ ...flags, clone_products: e.target.checked })}
          />
          <span>
            Copy {missing.length} product{missing.length === 1 ? "" : "s"} across — they
            don&apos;t exist in this business yet.
            <ul>
              {missing.map((p) => (
                <li key={p.product_id || p.code}>
                  {p.code ? `${p.code} · ` : ""}
                  {p.product_name || "product"}
                  {p.product_type ? ` (${p.product_type})` : ""}
                </li>
              ))}
            </ul>
          </span>
        </label>
      )}
      {runs.length > 0 && (
        <label className="tr-blocker">
          <input
            type="checkbox"
            checked={!!flags.remove_from_runs}
            onChange={(e) => setFlags({ ...flags, remove_from_runs: e.target.checked })}
          />
          <span>
            Remove the order from {runs.length} run sheet{runs.length === 1 ? "" : "s"} in
            the sending business.
            <ul>
              {runs.map((r) => (
                <li key={r.run_id}>
                  {r.delivery_date || "run"}
                  {r.drivers_name ? ` · ${r.drivers_name}` : ""}
                  {r.vehicle_number ? ` · ${r.vehicle_number}` : ""}
                </li>
              ))}
            </ul>
          </span>
        </label>
      )}
      {unclonable.length > 0 && (
        <div className="tr-blocker is-hard">
          <span>
            {unclonable.length} product{unclonable.length === 1 ? "" : "s"} no longer exist
            in the sending business and can&apos;t be copied. Create them here first, then
            accept again.
            <ul>
              {unclonable.map((p) => (
                <li key={p.product_id || p.code}>
                  {p.code ? `${p.code} · ` : ""}
                  {p.product_name || "product"}
                </li>
              ))}
            </ul>
          </span>
        </div>
      )}
    </div>
  );
}

function AcceptResult({ transfer, outgoing }) {
  const r = (transfer && transfer.result) || {};
  const cloned = Array.isArray(r.cloned_products) ? r.cloned_products.length : 0;
  const removed = Array.isArray(r.removed_from_runs) ? r.removed_from_runs.length : 0;
  return (
    <div className="tr-result">
      <b>
        {outgoing
          ? `Order moved to ${businessLabel(transfer.to_business)}.`
          : "Order moved to this business."}
      </b>
      <ul>
        {cloned > 0 && <li>{cloned} product{cloned === 1 ? "" : "s"} copied across</li>}
        {removed > 0 && (
          <li>Taken off {removed} run sheet{removed === 1 ? "" : "s"}</li>
        )}
        {r.parent_funeral && <li>Attached to its parent funeral</li>}
        {cloned === 0 && removed === 0 && !r.parent_funeral && <li>No extra changes were needed</li>}
      </ul>
    </div>
  );
}

/* ---------------- detail page ---------------- */

export function TransferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth() || {};
  const notif = useNotifications();
  const allowed = can(auth.user, PERM);

  // The notification that brought us here (if any) — enough to describe
  // the request to a member who cannot load the transfer itself.
  const fromNotification = (location.state && location.state.notification) || null;
  const hint = (fromNotification && fromNotification.data) || {};

  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(allowed);
  const [error, setError] = useState(null);

  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [blockers, setBlockers] = useState(null);
  const [flags, setFlags] = useState({});
  /* "reject" (destination) | "cancel" (source) — the inline are-you-sure. */
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(() => {
    if (!allowed || !id) return;
    setLoading(true);
    setError(null);
    fetchTransfer(id)
      .then((t) => setTransfer(t))
      .catch((e) => {
        const status = e && e.response && e.response.status;
        setError(
          status === 404
            ? "This transfer request no longer exists."
            : (e && e.response && e.response.data && e.response.data.message) ||
                "Could not load the transfer."
        );
      })
      .finally(() => setLoading(false));
  }, [allowed, id]);

  useEffect(() => {
    load();
  }, [load]);

  const pending = transfer && transfer.status === "pending";
  const outgoing = !!transfer && transfer.direction === "outgoing";
  const incoming = !outgoing;
  const canDecide = allowed && pending && incoming;
  const canWithdraw = allowed && pending && outgoing;

  const serial = (transfer && transfer.work_order_serial) || hint.work_order_serial || "";
  const orderType = (transfer && transfer.work_order_type) || hint.work_order_type || "";
  const from = (transfer && transfer.from_business) || hint.from_business || null;
  const to = (transfer && transfer.to_business) || null;
  /* The business at the other end of the move, whichever side we are. */
  const other = outgoing ? to : from;
  const listPath = outgoing ? "/transfers/outgoing" : "/transfers";

  const hardBlocked = !!(blockers && (blockers.unclonable_products || []).length);
  const needsTick =
    blockers &&
    ((blockers.missing_products && !flags.clone_products) ||
      (blockers.run_sheets && !flags.remove_from_runs));

  const finish = (updated, message) => {
    // Decision responses omit the (large) snapshot — keep the one we have
    // so the order preview stays on screen after the decision.
    setTransfer((prev) => ({
      ...(prev || {}),
      ...(updated || {}),
      snapshot: (updated && updated.snapshot) || (prev && prev.snapshot) || undefined,
    }));
    setBlockers(null);
    setFlags({});
    setConfirming(null);
    toast.success(message);
    transfersChanged();
    if (notif && notif.refresh) notif.refresh();
  };

  const onDecisionError = (e) => {
    const res = e && e.response;
    const status = res && res.status;
    const body = (res && res.data) || {};
    if (status === 409 && body.details && Object.keys(body.details).length) {
      setBlockers(body.details);
      return;
    }
    if (status === 404) {
      toast.warn("This request is no longer pending — it was decided or cancelled.");
      load();
      return;
    }
    if (status !== 403) {
      toast.error(body.message || "Something went wrong.");
    }
  };

  const accept = () => {
    setBusy(true);
    const body = { ...flags };
    if (note.trim()) body.note = note.trim();
    acceptTransfer(id, body)
      .then((updated) => finish(updated, `Work order ${serial} is now yours.`))
      .catch(onDecisionError)
      .finally(() => setBusy(false));
  };

  const reject = () => {
    setBusy(true);
    const body = note.trim() ? { note: note.trim() } : {};
    rejectTransfer(id, body)
      .then((updated) => finish(updated, `Transfer of ${serial} declined.`))
      .catch(onDecisionError)
      .finally(() => setBusy(false));
  };

  /* Source side: take the request back while it is still pending. */
  const withdraw = () => {
    setBusy(true);
    const body = note.trim() ? { note: note.trim() } : {};
    cancelTransfer(id, body)
      .then((updated) => finish(updated, `Request for ${serial} withdrawn.`))
      .catch(onDecisionError)
      .finally(() => setBusy(false));
  };

  return (
    <div className="tr-page">
      <header className="tr-head">
        <div className="tr-head-text">
          <button type="button" className="tr-crumb" onClick={() => navigate(listPath)}>
            <MdChevronLeft /> {outgoing ? "Sent transfers" : "Incoming transfers"}
          </button>
          <h1 className="tr-title">
            {serial ? `Work order ${serial}` : "Transfer request"}
            {transfer && <StatusPill status={transfer.status} />}
          </h1>
          <p className="tr-lede">
            {orderType ? `${orderType} order` : "Order"}{" "}
            {outgoing ? "sent to" : "from"} {businessLabel(other)}
            {transfer && transfer.requested_by
              ? ` · requested by ${actorName(transfer.requested_by)}${
                  transfer.requested_at ? ` ${timeAgo(transfer.requested_at)}` : ""
                }`
              : fromNotification && fromNotification.created_at
              ? ` · ${timeAgo(fromNotification.created_at)}`
              : ""}
          </p>
        </div>
        {transfer && transfer.status === "accepted" && incoming && (
          <div className="tr-head-actions">
            <button
              type="button"
              className="rf-btn rf-btn-primary"
              onClick={() => navigate(`/work-orders?order=${encodeURIComponent(serial)}`)}
            >
              Open work order
            </button>
          </div>
        )}
      </header>

      {!allowed ? (
        <div className="tr-grid">
          <div className="tr-col">
            {(hint.note || (fromNotification && fromNotification.message)) && (
              <div className="tr-card">
                <h3 className="tr-card-title">Request</h3>
                <p className="tr-note">{hint.note || fromNotification.message}</p>
              </div>
            )}
            <div className="tr-noperm">
              Accepting or declining a transfer needs the <b>transfer orders</b> access,
              which your account doesn&apos;t have. Ask an owner or admin of this business
              to action it.
            </div>
          </div>
        </div>
      ) : loading ? (
        <div className="tr-state">Loading…</div>
      ) : error ? (
        <div className="tr-state">
          <div className="nf-error" style={{ margin: 0 }}>{error}</div>
        </div>
      ) : transfer ? (
        <div className="tr-grid">
          <div className="tr-col">
            {text(transfer.note) && (
              <div className="tr-card">
                <h3 className="tr-card-title">
                  {outgoing ? "Note we sent" : `Note from ${businessLabel(from)}`}
                </h3>
                <p className="tr-note">{transfer.note}</p>
              </div>
            )}
            <OrderPreview transfer={transfer} />
          </div>

          <div className="tr-col">
            <div className="tr-card">
              <h3 className="tr-card-title">
                {outgoing ? `${businessLabel(to)}'s decision` : "Decision"}
              </h3>
              {transfer.status === "accepted" && (
                <AcceptResult transfer={transfer} outgoing={outgoing} />
              )}
              {transfer.status !== "pending" && (
                <p className="tr-decided" style={{ marginTop: transfer.status === "accepted" ? 12 : 0 }}>
                  <b style={{ textTransform: "capitalize" }}>{transfer.status}</b>
                  {transfer.decided_by && (transfer.decided_by.full_name || transfer.decided_by.userName)
                    ? ` by ${actorName(transfer.decided_by)}`
                    : ""}
                  {transfer.decided_at ? ` on ${fmtDateTime(transfer.decided_at)}` : ""}
                  {text(transfer.decision_note) ? ` — “${transfer.decision_note}”` : ""}
                </p>
              )}
              {canDecide && (
                <div className="tr-actions">
                  <p className="tr-help">
                    Accepting moves the order into this business and takes it out of{" "}
                    {businessLabel(from)}. The serial number is kept.
                  </p>
                  <textarea
                    className="tr-textarea"
                    placeholder="Optional note for the sending business"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={busy}
                  />
                  {blockers && <Blockers details={blockers} flags={flags} setFlags={setFlags} />}
                  {confirming !== "reject" ? (
                    <>
                      <button
                        type="button"
                        className="rf-btn rf-btn-primary"
                        disabled={busy || hardBlocked || !!needsTick}
                        onClick={accept}
                      >
                        {busy
                          ? "Working…"
                          : blockers
                          ? "Accept with these changes"
                          : "Accept transfer"}
                      </button>
                      <button
                        type="button"
                        className="rf-btn rf-btn-danger"
                        disabled={busy}
                        onClick={() => setConfirming("reject")}
                      >
                        Decline
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="tr-help">
                        Decline this request? The order stays with {businessLabel(from)}.
                      </p>
                      <button
                        type="button"
                        className="rf-btn rf-btn-danger"
                        disabled={busy}
                        onClick={reject}
                      >
                        {busy ? "Working…" : "Yes, decline"}
                      </button>
                      <button
                        type="button"
                        className="rf-btn rf-btn-ghost"
                        disabled={busy}
                        onClick={() => setConfirming(null)}
                      >
                        Keep it open
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Source side: we asked, they decide. All we can do is take
                  the request back while it is still pending. */}
              {canWithdraw && (
                <div className="tr-actions">
                  <p className="tr-help">
                    {businessLabel(to)} hasn&apos;t decided yet. The order is still ours
                    and still editable — later edits go across when they accept.
                  </p>
                  {confirming !== "cancel" ? (
                    <button
                      type="button"
                      className="rf-btn rf-btn-danger"
                      disabled={busy}
                      onClick={() => setConfirming("cancel")}
                    >
                      Withdraw request
                    </button>
                  ) : (
                    <>
                      <textarea
                        className="tr-textarea"
                        placeholder="Optional note for the other business"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        disabled={busy}
                      />
                      <button
                        type="button"
                        className="rf-btn rf-btn-danger"
                        disabled={busy}
                        onClick={withdraw}
                      >
                        {busy ? "Working…" : "Yes, withdraw it"}
                      </button>
                      <button
                        type="button"
                        className="rf-btn rf-btn-ghost"
                        disabled={busy}
                        onClick={() => setConfirming(null)}
                      >
                        Leave it with them
                      </button>
                    </>
                  )}
                </div>
              )}
              {outgoing && !pending && (
                <p className="tr-help">
                  {businessLabel(to)} was asked to take this order.
                </p>
              )}
            </div>

            <div className="tr-card">
              <h3 className="tr-card-title">Request</h3>
              <dl className="tr-kv" style={{ gridTemplateColumns: "1fr" }}>
                <KV
                  label={outgoing ? "Sent to" : "From"}
                  value={
                    other
                      ? `${other.name || ""}${other.code ? ` (${other.code})` : ""}`
                      : null
                  }
                />
                <KV label="Requested by" value={actorName(transfer.requested_by)} />
                <KV label="Requested" value={fmtDateTime(transfer.requested_at)} />
                <KV label="Order type" value={orderType} />
              </dl>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- list page ---------------- */

const LIST_FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "", label: "All" },
];

const TABS = [
  { dir: "incoming", label: "Incoming", to: "/transfers" },
  { dir: "outgoing", label: "Sent", to: "/transfers/outgoing" },
];

/** Both tabs of the transfers page; `direction` picks which one. */
export function TransfersPage({ direction = "incoming" }) {
  const navigate = useNavigate();
  const auth = useAuth() || {};
  const allowed = can(auth.user, PERM);
  const notif = useNotifications();
  const businessId = notif ? notif.businessId : null;
  const outgoing = direction === "outgoing";

  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(allowed);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  /* id of the row whose cancel is in flight */
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchTransfers({ direction, status: status || undefined })
      .then((list) => !cancelled && setRows(Array.isArray(list) ? list : []))
      .catch((e) => {
        if (cancelled) return;
        setRows([]);
        if (!(e && e.response && e.response.status === 403)) {
          setError("Could not load transfers.");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [allowed, direction, status, businessId, reload]);

  const pendingCount = useMemo(
    () => rows.filter((r) => r.status === "pending").length,
    [rows]
  );

  /* Withdraw straight from the row — the common case is "sent it to the
     wrong business", and opening the detail first adds nothing. */
  const withdrawRow = (row) => {
    setCancelling(row._id);
    cancelTransfer(row._id)
      .then(() => {
        toast.success(`Request for ${row.work_order_serial || "the order"} withdrawn.`);
        transfersChanged();
        setReload((n) => n + 1);
      })
      .catch((e) => {
        const res = (e && e.response) || {};
        if (res.status === 404) {
          toast.warn("That request was already decided.");
          setReload((n) => n + 1);
          return;
        }
        toast.error((res.data && res.data.message) || "Could not withdraw the request.");
      })
      .finally(() => setCancelling(null));
  };

  return (
    <div className="tr-page">
      <header className="tr-head">
        <div className="tr-head-text">
          <h1 className="tr-title">Transfers</h1>
          <p className="tr-lede">
            {outgoing
              ? "Work orders this business has asked others to take over."
              : "Work orders other businesses have asked you to take over."}
            {status === "pending" && rows.length > 0
              ? ` ${pendingCount} awaiting a decision.`
              : ""}
          </p>
        </div>
        <div className="tr-head-actions">
          <div className="rf-segmented">
            {LIST_FILTERS.map((f) => (
              <button
                key={f.key || "all"}
                type="button"
                className={status === f.key ? "is-active" : ""}
                onClick={() => setStatus(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="tr-tabs">
        {TABS.map((t) => (
          <button
            key={t.dir}
            type="button"
            className={`tr-tab${t.dir === direction ? " is-active" : ""}`}
            onClick={() => navigate(t.to)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!allowed ? (
        <div className="tr-state">
          <div className="tr-noperm">
            Viewing and actioning transfers needs the <b>transfer orders</b> access. Ask an
            owner or admin of this business.
          </div>
        </div>
      ) : (
        <>
          {error && <div className="nf-error" style={{ marginTop: 18 }}>{error}</div>}
          <div className="tr-list">
            {loading && rows.length === 0 ? (
              <div className="nf-empty">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="nf-empty">
                {status === "pending"
                  ? outgoing
                    ? "Nothing is waiting on another business."
                    : "No pending requests."
                  : "No transfers yet."}
              </div>
            ) : (
              <table className="tr-table">
                <thead>
                  <tr>
                    <th>Work order</th>
                    <th>Type</th>
                    <th>{outgoing ? "Sent to" : "From"}</th>
                    <th>Requested by</th>
                    <th>When</th>
                    <th>{outgoing ? "Outcome" : "Note"}</th>
                    <th>Status</th>
                    {outgoing && <th aria-label="Actions" />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr
                      key={t._id}
                      className="tr-row"
                      onClick={() => navigate(`/transfers/${t._id}`)}
                    >
                      <td className="tr-serial">{t.work_order_serial || "—"}</td>
                      <td>{t.work_order_type || "—"}</td>
                      <td>
                        {businessLabel(outgoing ? t.to_business : t.from_business)}
                      </td>
                      <td>{actorName(t.requested_by)}</td>
                      <td title={fmtDateTime(t.requested_at)}>{timeAgo(t.requested_at)}</td>
                      <td className="is-muted">
                        {outgoing
                          ? t.status === "pending"
                            ? text(t.note) || "—"
                            : [
                                t.decided_by ? actorName(t.decided_by) : "",
                                text(t.decision_note),
                              ]
                                .filter(Boolean)
                                .join(" — ") || "—"
                          : text(t.note) || "—"}
                      </td>
                      <td>
                        <StatusPill status={t.status} />
                      </td>
                      {outgoing && (
                        <td className="tr-rowact" onClick={(e) => e.stopPropagation()}>
                          {t.status === "pending" && (
                            <button
                              type="button"
                              className="rf-btn rf-btn-ghost"
                              disabled={cancelling === t._id}
                              onClick={() => withdrawRow(t)}
                            >
                              {cancelling === t._id ? "Working…" : "Withdraw"}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TransfersPage;
